import { useState, useEffect } from "react"

// @mui material components
import Grid from "@mui/material/Grid"

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox"

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout"
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar"
import Footer from "ScreenFooters/DefaultFooter"

// Overview page components
import Header from "ScreenLayouts/ProfileLayout/profile/components/Header"
import AccountInfo from "./components/AccountInfo"
import ActivityStatsCard from "./components/ActivityStatsCard"
import TenantInfoCard from "./components/TenantInfoCard"
import RecentActivitiesCard from "./components/RecentActivitiesCard"

// API
import { request } from "app/network"
import { UserProfile } from "app/types/UserProfile"
import { getTenantRole } from "Shared/Utils/CredentialStorage"

function MediInStepUserProfileScreen(): JSX.Element {
  const [userProfile, setUserProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadUserProfile()
  }, [])

  const loadUserProfile = async () => {
    try {
      setLoading(true)
      const response = await request("User.getCurrentUser", { paramz: {} })

      if (response.status === 200 && response.data) {
        const userData: UserProfile = response.data

        // 이름 또는 닉네임에서 첫 글자 추출
        const displayName = userData.name || userData.nickname || userData.id
        const firstLetter = displayName ? displayName.charAt(0).toUpperCase() : "U"

        // JWT 토큰에서 tenantRole 가져오기
        const tenantRoleFromToken = getTenantRole()

        // Header 컴포넌트에서 사용할 형식으로 변환
        const profileData = {
          imageUrl: userData.profileImageUrl || null, // null이면 MDAvatar가 children(첫 글자)을 표시
          nickName: displayName,
          role: userData.isAdmin ? "관리자" : "사용자",
          firstLetter: firstLetter, // 첫 글자 추가
          // 원본 데이터도 포함
          ...userData,
          // JWT 토큰에서 가져온 tenantRole 사용 (우선순위: 토큰 > 백엔드 응답 > 기본값)
          tenantRole: tenantRoleFromToken || (userData as any).role || userData.tenantRole || "USER",
          // 백엔드에서 'enable' 필드로 오는 값을 'isActive'로 명시적 매핑
          isActive: (userData as any).enable ?? userData.isActive ?? true,
          // 백엔드에서 'mobile' 필드로 오는 값을 'phone'으로 명시적 매핑
          phone: (userData as any).mobile || userData.phone || null
        }

        setUserProfile(profileData)
      }
    } catch (error) {
      console.error("사용자 정보 로드 실패:", error)
      // 에러 발생 시 기본값 설정
      setUserProfile({
        imageUrl: null,
        nickName: "사용자",
        role: "사용자",
        firstLetter: "U"
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mb={2} />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          로딩 중...
        </MDBox>
        <Footer />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header userProfile={userProfile}>
        <MDBox mt={5} mb={3}>
          {/* 상단 2열 레이아웃 */}
          <Grid container spacing={3} justifyContent="center">
            {/* 왼쪽: 계정 정보 */}
            <Grid item xs={12} lg={6}>
              {userProfile && <AccountInfo userProfile={userProfile} />}
            </Grid>

            {/* 오른쪽: 활동 통계 + 소속 조직 */}
            <Grid item xs={12} lg={6}>
              <Grid container spacing={3}>
                {/* 활동 통계 (상단) */}
                <Grid item xs={12}>
                  <ActivityStatsCard />
                </Grid>

                {/* 소속 조직 (하단) */}
                <Grid item xs={12}>
                  <TenantInfoCard userProfile={userProfile} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* 하단: 최근 활동 (전체 너비) */}
          <Grid container spacing={3} justifyContent="center" mt={2}>
            <Grid item xs={12} lg={12}>
              <RecentActivitiesCard />
            </Grid>
          </Grid>
        </MDBox>
      </Header>
      <Footer />
    </DashboardLayout>
  )
}

export default MediInStepUserProfileScreen
